import { mergeReferenceCatalogSections } from '@database/catalogRowHelpers.mjs';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/domain/GetSqlJsDatabase.ts';
import { unwrapSyncApiEnvelope } from '@src/modules/platform/sync/domain/syncApiEnvelope.ts';

const inflightRemoteByKey = new Map<string, Promise<unknown[]>>();

async function fetchRemoteBeneficiarioItems(
  apiBaseUrl: string,
  query: string,
  getToken: () => string | null,
): Promise<unknown[]> {
  const q = query.trim();
  const url = `${apiBaseUrl}/catalog/beneficiarios?q=${encodeURIComponent(q)}&limit=20`;
  const key = `${url}:${getToken() ?? ''}`;
  const existing = inflightRemoteByKey.get(key);
  if (existing) return existing as Promise<unknown[]>;

  const work = (async () => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(url, { headers, cache: 'no-store' });
    } catch {
      return [];
    }
    if (!response.ok) return [];

    let raw: unknown;
    try {
      raw = await response.json();
    } catch {
      return [];
    }
    const payload = unwrapSyncApiEnvelope(raw);
    return Array.isArray(payload.items) ? payload.items : [];
  })();

  inflightRemoteByKey.set(key, work);
  try {
    return await work;
  } finally {
    if (inflightRemoteByKey.get(key) === work) inflightRemoteByKey.delete(key);
  }
}

export async function searchBeneficiariosWithRemoteCache<
  T extends { publicId: string; documento: string; nombre: string },
>(opts: {
  query: string;
  searchLocal: () => Promise<T[]>;
  getDb: GetSqlJsDatabase;
  getToken: () => string | null;
  apiBaseUrl: string;
  markDirty?: () => void;
}): Promise<T[]> {
  const local = await opts.searchLocal();
  if (local.length > 0) return local;

  const q = opts.query.trim();
  if (q.length < 2 || !globalThis.navigator?.onLine) return [];

  const items = await fetchRemoteBeneficiarioItems(opts.apiBaseUrl, q, opts.getToken);
  if (items.length === 0) return [];

  const db = opts.getDb();
  if (!db) return [];
  try {
    mergeReferenceCatalogSections(db, { beneficiarios: items });
  } catch {
    return [];
  }
  opts.markDirty?.();
  return opts.searchLocal();
}
